
import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import marbleKitchen from "@/assets/marble-kitchen.jpg";
import marbleSink from "@/assets/marble-sink.jpg";
import marbleCounter from "@/assets/marble-counter.jpg";
import marbleTable from "@/assets/marble-table.jpg";
import marbleDecor from "@/assets/marble-decor.jpg";

const Marble = () => {
  const { language, isRTL } = useLanguage();

  const productSlugs = ["kitchen-surfaces", "sinks", "counters", "tables", "decorations"];

  const content = {
    ar: {
      title: "صناعة الرخام الصناعي",
      subtitle: "نقدم خدمات متكاملة في تصنيع وتركيب الرخام الصناعي لأسطح المطابخ والمغاسل وكونترات المحلات بأعلى معايير الجودة",
      productsTitle: "منتجاتنا من الرخام الصناعي",
      viewDetails: "عرض التفاصيل",
      features: [
        { title: "مقاومة للخدوش", description: "رخام صناعي عالي الجودة مقاوم للخدوش والكسر ويحافظ على مظهره لسنوات طويلة" },
        { title: "سهولة التنظيف", description: "أسطح غير مسامية تمنع تراكم البكتيريا وسهلة التنظيف والصيانة" },
        { title: "تصاميم متنوعة", description: "تشكيلة واسعة من الألوان والتصاميم التي تحاكي الرخام الطبيعي بتكلفة أقل" }
      ],
      products: [
        { title: "أسطح المطابخ الرخامية", description: "تصنيع أسطح رخامية فاخرة للمطابخ بتصاميم عصرية وألوان متنوعة من الرخام الصناعي عالي الجودة" },
        { title: "المغاسل الرخامية", description: "تصنيع مغاسل رخامية بتصاميم أنيقة للحمامات والمطابخ من الرخام الصناعي بمواصفات عالية ومقاومة للرطوبة" },
        { title: "كونترات المحلات", description: "تصنيع كونترات وأسطح عمل من الرخام الصناعي للمحلات التجارية والمكاتب بمواصفات عالية ومتانة فائقة" },
        { title: "طاولات رخامية", description: "تصنيع طاولات رخامية للمنازل والمطاعم والمكاتب بتصاميم فريدة وخيارات متعددة من الرخام الصناعي" },
        { title: "ديكورات رخامية", description: "تصنيع قطع ديكور وتحف من الرخام الصناعي منحوتة بدقة لإضفاء لمسة فخامة على المنازل والمكاتب" }
      ]
    },
    en: {
      title: "Artificial Marble Manufacturing",
      subtitle: "We offer comprehensive services in manufacturing and installing artificial marble for kitchen surfaces, sinks, and shop counters with the highest quality standards",
      productsTitle: "Our Artificial Marble Products",
      viewDetails: "View Details",
      features: [
        { title: "Scratch Resistant", description: "High-quality artificial marble resistant to scratches and breakage, maintaining its appearance for years" },
        { title: "Easy to Clean", description: "Non-porous surfaces preventing bacteria buildup and easy to clean and maintain" },
        { title: "Diverse Designs", description: "Wide range of colors and designs mimicking natural marble at a lower cost" }
      ],
      products: [
        { title: "Marble Kitchen Surfaces", description: "Manufacturing luxury marble surfaces for kitchens with modern designs and diverse colors from high-quality artificial marble" },
        { title: "Marble Sinks", description: "Manufacturing marble sinks with elegant designs for bathrooms and kitchens from artificial marble with high specifications and moisture resistance" },
        { title: "Shop Counters", description: "Manufacturing counters and work surfaces from artificial marble for commercial shops and offices with high specifications and superior durability" },
        { title: "Marble Tables", description: "Manufacturing marble tables for homes, restaurants, and offices with unique designs and multiple options from artificial marble" },
        { title: "Marble Decorations", description: "Manufacturing decorative pieces and artifacts from artificial marble precisely carved to add a touch of luxury to homes and offices" }
      ]
    }
  };

  const t = content[language];
  const images = [marbleKitchen, marbleSink, marbleCounter, marbleTable, marbleDecor];

  return (
    <div className="min-h-screen bg-gray-50 py-12" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-12">
          <h1 className="text-4xl font-kufi font-bold mb-6 text-primary">{t.title}</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">{t.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {t.features.map((feature, index) => (
            <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 * (index + 1) }} className="bg-white p-8 rounded-lg shadow-md text-center">
              <h3 className="text-xl font-kufi font-bold mb-4 text-primary">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <h2 className="text-2xl font-kufi font-bold text-center mb-8 text-primary">{t.productsTitle}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.products.map((product, index) => (
            <motion.div key={product.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                <img src={images[index]} alt={product.title} className="w-full h-48 object-cover" />
                <CardContent className="p-6 flex-grow flex flex-col">
                  <h3 className="text-xl font-kufi font-bold mb-2">{product.title}</h3>
                  <p className="text-gray-600 mb-4 flex-grow">{product.description}</p>
                  <Link to={`/marble/${productSlugs[index]}`}>
                    <Button className="bg-primary hover:bg-primary/90 w-full">{t.viewDetails}</Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Marble;
